/**
 * UI-mapped debug logging for posture classification (orientation, lateralization, overall).
 * Use logger.debug — enable debug level in the client to see output.
 */

import logger from '@/lib/logging';
import type { AnalysisData } from '../types';
import {
  getFrontalCoronalMetrics,
  getFrontalView,
  getRawValue,
  type parseLateralPostureMetrics,
} from './posture-analysis-metrics-parse';

export type SignalUiMeta = {
  uiMetric: string | null;
  shownInUi: boolean;
  comparesTo?: string;
};

/** Orientation strategy (hip displacement) signal → UI report row */
export const HIP_DISPLACEMENT_SIGNAL_UI_MAP: Record<string, SignalUiMeta> = {
  'sb.hipFwd.markedAnterior': {
    uiMetric: 'Hip Anterior Shift',
    shownInUi: true,
    comparesTo: 'threshold.hipForwardCmMin',
  },
  'sb.hipFwd.mildAnterior': {
    uiMetric: 'Hip Anterior Shift',
    shownInUi: true,
    comparesTo: 'threshold.strictHip',
  },
  'sb.torso.posteriorShouldersStrong': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal.shoulderMinusHipOffsetCm vs threshold.trunkLeanLatDegMin',
  },
  'sb.torso.posteriorShouldersMild': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal.shoulderMinusHipOffsetCm vs threshold.strictLean',
  },
  'sb.knee.hyperextension': {
    uiMetric: 'Knee State',
    shownInUi: true,
    comparesTo: 'Knee State → Hyperext',
  },
  'sb.pelvis.posteriorTilt': {
    uiMetric: 'Pelvic Tilt (ASIS-PSIS)',
    shownInUi: true,
    comparesTo: 'threshold.strictTilt (posterior)',
  },
  'apt.pelvis.anteriorTilt': {
    uiMetric: 'Pelvic Tilt (ASIS-PSIS)',
    shownInUi: true,
    comparesTo: 'threshold.pelvicTiltDegMin',
  },
  'apt.torso.anteriorShouldersStrong': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal.shoulderMinusHipOffsetCm vs threshold.trunkLeanLatDegMin',
  },
  'apt.torso.anteriorShouldersMild': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal.shoulderMinusHipOffsetCm vs threshold.strictLean',
  },
  'apt.knee.hyperextension': {
    uiMetric: 'Knee State',
    shownInUi: true,
    comparesTo: 'Knee State → Hyperext',
  },
  'apt.hip.notMarkedAnterior': {
    uiMetric: 'Hip Anterior Shift',
    shownInUi: true,
    comparesTo: 'below threshold.hipForwardCmMin (supports APT)',
  },
};

export const HIP_SWAYBACK_POSSIBLE_SIGNALS = [
  'sb.hipFwd.markedAnterior',
  'sb.hipFwd.mildAnterior',
  'sb.torso.posteriorShouldersStrong',
  'sb.torso.posteriorShouldersMild',
  'sb.knee.hyperextension',
  'sb.pelvis.posteriorTilt',
] as const;

export const HIP_APT_POSSIBLE_SIGNALS = [
  'apt.pelvis.anteriorTilt',
  'apt.torso.anteriorShouldersStrong',
  'apt.torso.anteriorShouldersMild',
  'apt.knee.hyperextension',
  'apt.hip.notMarkedAnterior',
] as const;

/** Clinical score rules strategy */
export const CLINICAL_SCORE_SIGNAL_UI_MAP: Record<string, SignalUiMeta> = {
  'clinical.apt.pelvis.anteriorTilt': {
    uiMetric: 'Pelvic Tilt (ASIS-PSIS)',
    shownInUi: true,
    comparesTo: 'threshold.pelvicTiltDegMin (+3 aptScore)',
  },
  'clinical.apt.pelvis.mildAnterior': {
    uiMetric: 'Pelvic Tilt (ASIS-PSIS)',
    shownInUi: true,
    comparesTo: 'threshold.strictTilt (reason only)',
  },
  'clinical.apt.globalForwardLean': {
    uiMetric: 'Shoulder Anterior Shift',
    shownInUi: true,
    comparesTo: 'Shoulder > strictHip and > hip + strictLean',
  },
  'clinical.apt.knee.hyperextension': {
    uiMetric: 'Knee State',
    shownInUi: true,
    comparesTo: 'Knee State → Hyperext',
  },
  'clinical.apt.torso.forward': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal shoulderMinusHip vs trunkLeanLatDegMin',
  },
  'clinical.apt.torso.forwardMild': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal shoulderMinusHip vs strictLean',
  },
  'clinical.sb.hip.markedAnterior': {
    uiMetric: 'Hip Anterior Shift',
    shownInUi: true,
    comparesTo: 'threshold.hipForwardCmMin (when not leaning forward)',
  },
  'clinical.sb.hip.mildAnterior': {
    uiMetric: 'Hip Anterior Shift',
    shownInUi: true,
    comparesTo: 'threshold.strictHip',
  },
  'clinical.sb.knee.flexion': {
    uiMetric: 'Knee State',
    shownInUi: true,
    comparesTo: 'Knee State → Flex',
  },
  'clinical.sb.torso.sway': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal shoulderMinusHip < -trunkLeanLatDegMin',
  },
  'clinical.sb.torso.swayMild': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'internal shoulderMinusHip < -strictLean',
  },
};

export const CLINICAL_APT_POSSIBLE_SIGNALS = [
  'clinical.apt.pelvis.anteriorTilt',
  'clinical.apt.pelvis.mildAnterior',
  'clinical.apt.globalForwardLean',
  'clinical.apt.knee.hyperextension',
  'clinical.apt.torso.forward',
  'clinical.apt.torso.forwardMild',
] as const;

export const CLINICAL_SB_POSSIBLE_SIGNALS = [
  'clinical.sb.hip.markedAnterior',
  'clinical.sb.hip.mildAnterior',
  'clinical.sb.knee.flexion',
  'clinical.sb.torso.sway',
  'clinical.sb.torso.swayMild',
] as const;

/** Visual Pattern Recognition strategy — extends hip-displacement with FHP, tibial, and practitioner observations */
export const HIP_VPR_SIGNAL_UI_MAP: Record<string, SignalUiMeta> = {
  ...HIP_DISPLACEMENT_SIGNAL_UI_MAP,
  'vpr.sb.forwardHeadPosture': {
    uiMetric: 'Craniovertebral (Head Ant)',
    shownInUi: true,
    comparesTo: 'threshold.fhpSwaybackDegMin',
  },
  'vpr.sb.posteriorTibialInclination': {
    uiMetric: 'Tibial Inclination',
    shownInUi: true,
    comparesTo: 'threshold.tibialPostThresholdDeg (posterior)',
  },
  'vpr.sb.obs.poochBelly': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'practitioner toggle',
  },
  'vpr.sb.obs.flatGlutes': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'practitioner toggle',
  },
  'vpr.apt.neutralHeadPosture': {
    uiMetric: 'Craniovertebral (Head Ant)',
    shownInUi: true,
    comparesTo: 'below threshold.fhpSwaybackDegMin + pelvicTiltDegMin gate',
  },
  'vpr.apt.anteriorTibialInclination': {
    uiMetric: 'Tibial Inclination',
    shownInUi: true,
    comparesTo: 'threshold.tibialAntThresholdDeg (anterior)',
  },
  'vpr.apt.obs.ribFlare': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'practitioner toggle',
  },
  'vpr.apt.obs.flatCervicalCurve': {
    uiMetric: null,
    shownInUi: false,
    comparesTo: 'practitioner toggle',
  },
};

export const HIP_VPR_SWAYBACK_POSSIBLE_SIGNALS = [
  ...HIP_SWAYBACK_POSSIBLE_SIGNALS,
  'vpr.sb.forwardHeadPosture',
  'vpr.sb.posteriorTibialInclination',
  'vpr.sb.obs.poochBelly',
  'vpr.sb.obs.flatGlutes',
] as const;

export const HIP_VPR_APT_POSSIBLE_SIGNALS = [
  ...HIP_APT_POSSIBLE_SIGNALS,
  'vpr.apt.neutralHeadPosture',
  'vpr.apt.anteriorTibialInclination',
  'vpr.apt.obs.ribFlare',
  'vpr.apt.obs.flatCervicalCurve',
] as const;

/** Lateralization checks in computePostureDiagnoses */
export const LATERALIZATION_SIGNAL_UI_MAP: Record<string, SignalUiMeta> = {
  'lat.head.tiltLeft': {
    uiMetric: 'Head tilt',
    shownInUi: true,
    comparesTo: 'threshold.latAngleThresholdDeg',
  },
  'lat.head.tiltRight': {
    uiMetric: 'Head tilt',
    shownInUi: true,
    comparesTo: 'threshold.latAngleThresholdDeg',
  },
  'lat.shoulder.rightLower': {
    uiMetric: 'Shoulders level',
    shownInUi: true,
    comparesTo: 'threshold.latAngleThresholdDeg',
  },
  'lat.shoulder.leftLower': {
    uiMetric: 'Shoulders level',
    shownInUi: true,
    comparesTo: 'threshold.latAngleThresholdDeg',
  },
  'lat.hip.rightLower': {
    uiMetric: 'Pelvis level',
    shownInUi: true,
    comparesTo: 'threshold.latAngleThresholdDeg',
  },
  'lat.hip.leftLower': {
    uiMetric: 'Pelvis level',
    shownInUi: true,
    comparesTo: 'threshold.latAngleThresholdDeg',
  },
  'lat.pelvic.shiftRight': {
    uiMetric: 'Pelvic shift',
    shownInUi: true,
    comparesTo: 'threshold.latShiftThresholdCm',
  },
  'lat.pelvic.shiftLeft': {
    uiMetric: 'Pelvic shift',
    shownInUi: true,
    comparesTo: 'threshold.latShiftThresholdCm',
  },
  'lat.foot.posteriorCalc': {
    uiMetric: 'Calcaneus tilt (L/R)',
    shownInUi: true,
    comparesTo: 'Posterior view — weighted 0.75 into latScore',
  },
};

export const LATERALIZATION_POSSIBLE_SIGNALS = [
  'lat.head.tiltLeft',
  'lat.head.tiltRight',
  'lat.shoulder.rightLower',
  'lat.shoulder.leftLower',
  'lat.hip.rightLower',
  'lat.hip.leftLower',
  'lat.pelvic.shiftRight',
  'lat.pelvic.shiftLeft',
  'lat.foot.posteriorCalc',
] as const;

/** Display signals appended in computePostureDiagnoses (badges / summary) */
export const DISPLAY_SIGNAL_UI_MAP: Record<string, SignalUiMeta> = {
  'display.hipAntCm': { uiMetric: 'Hip Anterior Shift', shownInUi: true },
  'display.kneeFlexion': { uiMetric: 'Knee State', shownInUi: true },
  'display.torsoSway': { uiMetric: null, shownInUi: false },
  'display.pelvicTilt': { uiMetric: 'Pelvic Tilt (ASIS-PSIS)', shownInUi: true },
  'display.kneesExtended': { uiMetric: 'Knee State', shownInUi: true },
  'display.torsoAntLean': { uiMetric: null, shownInUi: false },
  'display.stacked': { uiMetric: null, shownInUi: false },
  'display.forwardHead': { uiMetric: 'Craniovertebral (Head Ant)', shownInUi: true },
  'display.lateralizationLabel': { uiMetric: null, shownInUi: false },
};

export const DISPLAY_POSSIBLE_SIGNALS = Object.keys(DISPLAY_SIGNAL_UI_MAP);

export function formatSignalsFired(fired: number, total: number) {
  return `${fired} of ${total} signals fired`;
}

export function mapSignalsForUi(
  activeSignals: string[],
  signalUiMap: Record<string, SignalUiMeta>
) {
  return activeSignals.map((key) => ({
    signalKey: key,
    ...signalUiMap[key],
  }));
}

export function buildSignalsFiredSummary(
  activeSignals: string[],
  groups: { label: string; prefix: string; possible: readonly string[] }[]
) {
  const byGroup = groups.map((g) => {
    const fired = activeSignals.filter((k) => k.startsWith(g.prefix)).length;
    return {
      label: g.label,
      fired,
      totalPossible: g.possible.length,
      summary: formatSignalsFired(fired, g.possible.length),
    };
  });
  const totalFired = activeSignals.length;
  const totalPossible = groups.reduce((n, g) => n + g.possible.length, 0);
  return {
    groups: byGroup,
    combined: {
      fired: totalFired,
      totalPossible,
      summary: formatSignalsFired(totalFired, totalPossible),
    },
  };
}

export function logPostureClassificationDebug(
  logPrefix: string,
  message: string,
  payload: Record<string, unknown>
) {
  logger.debug(`${logPrefix} ${message}`, payload);
}

export function buildOrientationUiMappedInputs(opts: {
  analysisData: AnalysisData;
  parsedViewSide: 'Left' | 'Right' | null;
  clientSex: 'Male' | 'Female' | 'Unknown';
  fromLandmarks: boolean;
  hipAnteriorShiftCm: number;
  shoulderAnteriorShiftCm: number;
  shoulderMinusHipOffsetCm: number;
  kneeHyperextension: boolean;
  kneeFlexion: boolean | null;
  pelvicTiltDeg: number;
  parsed: NonNullable<ReturnType<typeof parseLateralPostureMetrics>>;
  pelvicTiltDegMin: number;
  rules: Record<string, number>;
  kneeHyperextensionDegMin: number;
  hasAsisPsisLine: boolean;
  frontalViewForContext?: string;
}) {
  const lateralReport = opts.parsedViewSide
    ? `${opts.parsedViewSide} View Report`
    : 'Left/Right View Report';
  const src = (field: string) =>
    opts.fromLandmarks ? 'landmarks (live capture)' : `parsed metric string (${field})`;

  const frontalView = getFrontalView(opts.analysisData);
  const frontal = getFrontalCoronalMetrics(opts.analysisData, frontalView);
  const trunkLeanDeg = getRawValue(opts.analysisData, frontalView, 'Trunk lean');

  return {
    orientationInputs: {
      [`${lateralReport} — Hip Anterior Shift`]: {
        shownInUi: true,
        unit: 'cm',
        value: opts.hipAnteriorShiftCm,
        dataSource: src('Hip Anterior Shift'),
      },
      [`${lateralReport} — Shoulder Anterior Shift`]: {
        shownInUi: true,
        unit: 'cm',
        value: opts.shoulderAnteriorShiftCm,
        dataSource: src('Shoulder Anterior Shift'),
      },
      [`${lateralReport} — Pelvic Tilt (ASIS-PSIS)`]: {
        shownInUi: true,
        unit: '°',
        value: opts.pelvicTiltDeg,
        dataSource: src('Pelvic Tilt'),
      },
      [`${lateralReport} — Knee State (classification uses)`]: {
        shownInUi: true,
        unit: '°',
        note: 'UI shows "N° Flex" or "N° Hyperext"; strategy uses boolean flags below',
        kneeHyperextension: opts.kneeHyperextension,
        kneeFlexion: opts.kneeFlexion,
        dataSource: src('Knee State'),
      },
      'internal — shoulderMinusHipOffsetCm (not a report row)': {
        shownInUi: false,
        unit: 'cm',
        value: opts.shoulderMinusHipOffsetCm,
        formula: 'Shoulder Anterior Shift − Hip Anterior Shift',
        dataSource: src('derived from Shoulder + Hip shifts'),
        note: 'NOT the same as Anterior "Trunk lean" (°)',
      },
    },
    otherReportRows_notUsedForSbApt: {
      [`${lateralReport} — Craniovertebral (Head Ant)`]: { shownInUi: true, usedByThisStrategy: false },
      [`${lateralReport} — Knee-Hip Offset`]: {
        shownInUi: true,
        usedByThisStrategy: false,
        note: 'Shown on lateral report; does not affect SB/APT in orientation strategies',
      },
      [`${lateralReport} — Tibial Inclination`]: { shownInUi: true, usedByThisStrategy: false },
      [`${frontalView} View Report — Head tilt`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(frontal.headTilt) ? frontal.headTilt : null,
        usedByThisStrategy: false,
      },
      [`${frontalView} View Report — Shoulders level`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(frontal.shoulderLevel) ? frontal.shoulderLevel : null,
        usedByThisStrategy: false,
      },
      [`${frontalView} View Report — Pelvis level`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(frontal.hipLevel) ? frontal.hipLevel : null,
        usedByThisStrategy: false,
      },
      [`${frontalView} View Report — Trunk lean`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(trunkLeanDeg) ? trunkLeanDeg : null,
        usedByThisStrategy: false,
      },
      [`${frontalView} View Report — Pelvic shift`]: {
        shownInUi: true,
        unit: 'cm',
        value: isFinite(frontal.pelvicShift) ? frontal.pelvicShift : null,
        usedByThisStrategy: false,
        note: 'Used for lateralization in computePostureDiagnoses',
      },
    },
    thresholds: {
      'threshold.hipForwardCmMin → Hip Anterior Shift (swayback)': {
        value: opts.rules.hipForwardCmMin,
        unit: 'cm',
      },
      'threshold.strictHip → Hip Anterior Shift (mild swayback)': {
        value: opts.rules.strictHip,
        unit: 'cm',
      },
      'threshold.strictLean → internal shoulderMinusHipOffsetCm': {
        value: opts.rules.strictLean,
        unit: 'cm',
      },
      'threshold.trunkLeanLatDegMin → internal shoulderMinusHipOffsetCm (not frontal Trunk lean °)': {
        value: opts.rules.trunkLeanLatDegMin,
        unit: 'cm',
      },
      'threshold.strictTilt → Pelvic Tilt (posterior, swayback)': {
        value: opts.rules.strictTilt,
        unit: '°',
      },
      'threshold.pelvicTiltDegMin → Pelvic Tilt (APT)': {
        value: opts.pelvicTiltDegMin,
        unit: '°',
        clientSex: opts.clientSex,
      },
      'threshold.kneeHyperextensionDegMin → Knee State label': {
        value: opts.kneeHyperextensionDegMin,
        unit: '°',
      },
    },
    meta: {
      clientSex: opts.clientSex,
      primaryLateralView: opts.parsedViewSide,
      hasAsisPsisLine: opts.hasAsisPsisLine,
      frontalViewForReference: opts.frontalViewForContext ?? frontalView,
    },
    parsedMetricFallback: opts.fromLandmarks
      ? null
      : {
          'Hip Anterior Shift (parsed)': opts.parsed.hipFwdVal,
          'Shoulder Anterior Shift (parsed)': opts.parsed.shoFwdVal,
          'internal shoulderMinusHip (parsed)': opts.parsed.sagittalTorsoOffset,
          'Knee State isKneeFlexed': opts.parsed.isKneeFlexed,
          'Knee State isKneeHyperext': opts.parsed.isKneeHyperext,
        },
  };
}

export function buildLateralizationDebugInputs(opts: {
  frontalView: 'Anterior' | 'Posterior';
  headTilt: number;
  shoulderLevel: number;
  hipLevel: number;
  pelvicShift: number;
  latAngleThreshold: number;
  latShiftThreshold: number;
  hasPosterior: boolean;
  calcL?: number;
  calcR?: number;
}) {
  const fv = opts.frontalView;
  return {
    lateralizationInputs: {
      [`${fv} View Report — Head tilt`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(opts.headTilt) ? opts.headTilt : null,
        threshold: opts.latAngleThreshold,
      },
      [`${fv} View Report — Shoulders level`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(opts.shoulderLevel) ? opts.shoulderLevel : null,
        threshold: opts.latAngleThreshold,
      },
      [`${fv} View Report — Pelvis level`]: {
        shownInUi: true,
        unit: '°',
        value: isFinite(opts.hipLevel) ? opts.hipLevel : null,
        threshold: opts.latAngleThreshold,
      },
      [`${fv} View Report — Pelvic shift`]: {
        shownInUi: true,
        unit: 'cm',
        value: isFinite(opts.pelvicShift) ? opts.pelvicShift : null,
        threshold: opts.latShiftThreshold,
      },
      ...(opts.hasPosterior
        ? {
            'Posterior View Report — Calcaneus tilt (L)': {
              shownInUi: true,
              unit: '°',
              value: opts.calcL ?? null,
            },
            'Posterior View Report — Calcaneus tilt (R)': {
              shownInUi: true,
              unit: '°',
              value: opts.calcR ?? null,
            },
          }
        : {}),
    },
    thresholds: {
      latAngleThresholdDeg: opts.latAngleThreshold,
      latShiftThresholdCm: opts.latShiftThreshold,
    },
  };
}

export function logLateralizationDecisionDebug(
  logPrefix: string,
  opts: {
    activeSignals: string[];
    latScore: number;
    lateralized: string;
    latReason: string[];
    lateralizationConfidencePct: number;
    groundingSide?: 'Left' | 'Right';
    groundingConfidencePct: number;
  }
) {
  logPostureClassificationDebug(logPrefix, 'lateralization signals & decision (UI-mapped)', {
    signalsFired: buildSignalsFiredSummary(opts.activeSignals, [
      { label: 'lateralization', prefix: 'lat.', possible: LATERALIZATION_POSSIBLE_SIGNALS },
    ]),
    lateralization: {
      latScore: opts.latScore,
      signalsFired: formatSignalsFired(
        opts.activeSignals.length,
        LATERALIZATION_POSSIBLE_SIGNALS.length
      ),
      activeSignals: mapSignalsForUi(opts.activeSignals, LATERALIZATION_SIGNAL_UI_MAP),
      reasons: opts.latReason,
    },
    decision: {
      lateralized: opts.lateralized,
      lateralizationConfidencePct: opts.lateralizationConfidencePct,
      groundingSide: opts.groundingSide ?? null,
      groundingConfidencePct: opts.groundingConfidencePct,
    },
  });
}

export function logDisplaySignalsDebug(
  logPrefix: string,
  opts: {
    activeSignals: string[];
    displayStrings: string[];
    pattern: string;
    orientation: string;
  }
) {
  logPostureClassificationDebug(logPrefix, 'display signals (UI-mapped)', {
    signalsFired: buildSignalsFiredSummary(opts.activeSignals, [
      { label: 'display', prefix: 'display.', possible: DISPLAY_POSSIBLE_SIGNALS },
    ]),
    activeSignals: mapSignalsForUi(opts.activeSignals, DISPLAY_SIGNAL_UI_MAP),
    displayStrings: opts.displayStrings,
    pattern: opts.pattern,
    orientation: opts.orientation,
  });
}

export function logOverallFusionDebug(
  logPrefix: string,
  opts: {
    inputs: Record<string, unknown>;
    pattern: { value: string; confidence: number; path: string };
    lateralization: {
      lateralized: string | null;
      grounded: string | null;
      confidence: number;
      path: string;
    };
    phase: { value: string; path: string };
    shortCode: string;
  }
) {
  logPostureClassificationDebug(logPrefix, 'overall fusion (SoP + image)', {
    inputs: opts.inputs,
    pattern: opts.pattern,
    lateralization: opts.lateralization,
    phase: opts.phase,
    shortCode: opts.shortCode,
  });
}

export function logOrientationDecisionDebug(
  logPrefix: string,
  opts: {
    signalUiMap: Record<string, SignalUiMeta>;
    activeSignals: string[];
    signalGroups: { label: string; prefix: string; possible: readonly string[] }[];
    scoreByGroup?: Record<string, { pts: number }>;
    decisionPath: string;
    orientation: string;
    matchedPoints: number;
    maxPoints: number;
    tieInputs?: Record<string, unknown> | null;
    finalEvidence: string[];
  }
) {
  const scoreDetail: Record<string, unknown> = {};
  for (const g of opts.signalGroups) {
    const fired = opts.activeSignals.filter((k) => k.startsWith(g.prefix));
    scoreDetail[g.label] = {
      ...(opts.scoreByGroup?.[g.label] ?? {}),
      signalsFired: formatSignalsFired(fired.length, g.possible.length),
      activeSignals: mapSignalsForUi(fired, opts.signalUiMap),
    };
  }

  logPostureClassificationDebug(logPrefix, 'classification signals & decision (UI-mapped)', {
    signalsFired: buildSignalsFiredSummary(opts.activeSignals, opts.signalGroups),
    ...scoreDetail,
    decision: {
      path: opts.decisionPath,
      orientation: opts.orientation,
      matchedPoints: opts.matchedPoints,
      confidenceDenominator: opts.maxPoints,
      tieInputs: opts.tieInputs ?? null,
    },
    finalEvidence: opts.finalEvidence,
  });
}
